import { Col, FlexView, Row } from '@cuemath/comps';
import { Button, Separator, Text } from '@cuemath/web-comps';
import React, { Fragment } from 'react';
import useResultSuccessLogic from './ResultSuccess.logic';
import LAYOUT from 'constants/layout';
import ResultNotAGoodMatch from '../result-not-a-good-match';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import TeacherDetails from '../teacher-details';

const { GUTTER } = LAYOUT;

const TeacherAttribute = styled(Row)`
  margin: 0 -${GUTTER / 2}px;
  margin-right: ${GUTTER}px;
  margin-bottom: 4px;
`;
const SecondaryButton = styled(Button)`
  margin-right: 16px;
`;
const ScrollWrapper = styled(FlexView)`
  max-height: 370px;
  overflow-y: auto;
`;

const ResultSuccess = ({
  mappingSearchResult,
  showOtherTeachers,
  setShowOtherTeachers,
  dateTimePreset,
  filters,
  prospectId,
}) => {
  const { mapperId, studentId, studentName, teachers } = mappingSearchResult ?? {};
  const bestTeacher = teachers.length > 0 ? teachers[0] : {};
  const otherTeachers = teachers.length > 1 ? teachers.slice(1) : [];

  const {
    selectedTeacher,
    isNotGoodMatch,
    handleSelectTeacher,
    setIsNotGoodMatch,
    handleAssignTeacher,
    isBusy,
  } = useResultSuccessLogic({
    mapperId,
    prospectId,
    showOtherTeachers,
    bestTeacher,
    otherTeachers,
    dateTimePreset,
  });
  const studentAttributes = [
    {
      label: 'Student id',
      value: (
        <Button
          label={studentId}
          size="small"
          variant="tertiaryWithUnderline"
          onClick={() => window.open(`/students/${studentId}`, '_blank')}
        />
      ),
    },
    {
      label: 'Student name',
      value: studentName,
    },
  ];

  const renderTeacherItem = ({
    id,
    name,
    score,
    enrollments,
    sopScore,
    studentDensity,
    plusPacToPaid,
    basePacToPaid,
  }) => (
    <TeacherDetails
      id={id}
      name={name}
      score={score}
      enrollments={enrollments}
      sopScore={sopScore}
      studentDensity={studentDensity}
      plusPacToPaid={plusPacToPaid}
      basePacToPaid={basePacToPaid}
      onPress={handleSelectTeacher}
      selectedTeacher={selectedTeacher}
    />
  );
  const teacherStatus = showOtherTeachers
    ? 'Other teachers found:'
    : 'Best teacher found:';
  const renderResultSuccess = (
    <>
      {studentAttributes?.map(({ label, value }) => (
        <Fragment key={label}>
          <TeacherAttribute>
            <Col>
              <Text variant="body2">{`${label}: `}</Text>
            </Col>
            <Col gutter={0}>
              <Text variant="body1Medium">{value}</Text>
            </Col>
          </TeacherAttribute>
          <Separator height={GUTTER / 4} />
        </Fragment>
      ))}
      <Separator height={GUTTER / 4} />
      <Text variant="body1Medium">{teacherStatus}</Text>
      <Separator height={GUTTER / 2} />
      <ScrollWrapper>
        {showOtherTeachers ? (
          otherTeachers?.map(teacher => renderTeacherItem?.(teacher ?? {}))
        ) : (
          <>
            <FlexView>{renderTeacherItem?.(bestTeacher ?? {})}</FlexView>
          </>
        )}
      </ScrollWrapper>
      <Separator height={GUTTER} />
      <FlexView alignItems="center" flexDirection="row" justifyContent="center">
        {!showOtherTeachers && (
          <SecondaryButton
            label="Not a good match"
            size="small"
            variant="secondary"
            onClick={() => setIsNotGoodMatch(true)}
          />
        )}
        <Button
          isBusy={isBusy}
          size="small"
          label="Assign teacher"
          onClick={() => handleAssignTeacher(selectedTeacher, dateTimePreset, filters)}
          isDisabled={!selectedTeacher}
        />
      </FlexView>
    </>
  );

  return (
    <FlexView>
      {isNotGoodMatch ? (
        <ResultNotAGoodMatch
          result={mappingSearchResult}
          showOtherTeachers={showOtherTeachers}
          setShowOtherTeachers={setShowOtherTeachers}
          dateTimePreset={dateTimePreset}
          filters={filters}
          prospectId={prospectId}
        />
      ) : (
        renderResultSuccess
      )}
    </FlexView>
  );
};

ResultSuccess.propsTypes = {
  prospectId: PropTypes.string,
  requestedTimeslots: PropTypes.arrayOf(PropTypes.number),
  mappingId: PropTypes.string,
  teacherId: PropTypes.string,
  teacherName: PropTypes.string,
  cumulativeScore: PropTypes.string,
  studentName: PropTypes.string,
  studentId: PropTypes.string,
};

export default ResultSuccess;
